import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../i18n/i18n';
import { COMMENT_KIND_LABEL, addComment, fetchComments, markAnswered, type Comment, type CommentKind, type SubjectType } from './api';

export default function Comments({ learnerId, subjectType, subjectId, onChange }: { learnerId: string; subjectType: SubjectType; subjectId: string; onChange?: () => void }) {
  const { t, lang } = useLanguage();
  const [list, setList] = useState<Comment[] | null>(null);
  const [kind, setKind] = useState<CommentKind>('NOTE');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchComments(learnerId, subjectType, subjectId).then(setList).catch((e) => setError(e instanceof Error ? e.message : t('Comments could not be loaded.')));
  }, [learnerId, subjectType, subjectId, t]);

  useEffect(() => { load(); }, [load]);

  async function send() {
    setBusy(true);
    setError(null);
    try {
      await addComment(learnerId, subjectType, subjectId, kind, text.trim());
      setText('');
      setKind('NOTE');
      load();
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(c: Comment) {
    try {
      await markAnswered(learnerId, c.id, !c.answered);
      load();
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
    }
  }

  return (
    <section className="planner-comments" aria-label={t('Comments')}>
      <h3>{t('Comments')}</h3>
      {list && list.length === 0 && <p className="muted">{t('No comments yet.')}</p>}
      {list && list.length > 0 && (
        <ul className="planner-comment-list">
          {list.map((c) => (
            <li key={c.id} className={`planner-comment is-${c.authorRole.toLowerCase()}`}>
              <div className="planner-comment-head">
                <strong>{c.authorName ?? t('Someone')}</strong>
                <span className="pill">{c.authorRole === 'CONSULTANT' ? t('Consultant') : t('Parent')}</span>
                <span className="pill">{t(COMMENT_KIND_LABEL[c.kind])}</span>
                <span className="muted">{new Date(c.createdAt).toLocaleString(lang === 'en' ? 'en-GB' : lang, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <p>{c.text}</p>
              {c.kind === 'QUESTION' && (
                <button type="button" className="btn ghost" onClick={() => void toggle(c)}>
                  {c.answered ? t('Answered. Mark as not answered') : t('Mark as answered')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <div className="error" role="alert">{error}</div>}
      <div className="field">
        <label htmlFor={`c-kind-${subjectId}`}>{t('Kind of comment')}</label>
        <select id={`c-kind-${subjectId}`} value={kind} onChange={(e) => setKind(e.target.value as CommentKind)}>
          {(Object.keys(COMMENT_KIND_LABEL) as CommentKind[]).map((k) => <option key={k} value={k}>{t(COMMENT_KIND_LABEL[k])}</option>)}
        </select>
      </div>
      <div className="field">
        <label htmlFor={`c-text-${subjectId}`}>{t('Your comment')}</label>
        <textarea id={`c-text-${subjectId}`} rows={3} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="toolbar">
        <button type="button" className="btn secondary" disabled={busy || !text.trim()} onClick={() => void send()}>{t('Add comment')}</button>
      </div>
    </section>
  );
}
